(() => {
  class ParallaxScrollAnimation extends HTMLElement {
    connectedCallback() {
      if (this.__psaInitialized) return;
      this.__psaInitialized = true;

      this.items = Array.from(this.querySelectorAll("parallax-scroll-item")).slice(0, 4);
      if (this.items.length < 4) {
        console.warn("[parallax-scroll-animation] Please provide 4 parallax-scroll-item elements.");
        return;
      }

      this.render();
      this.initAnimation();
    }

    getItemData(item) {
      const img = item.querySelector("img");
      const heading = item.querySelector("[slot='heading'], h4, h3")?.innerHTML?.trim() || "";
      const paragraphs = Array.from(item.querySelectorAll("[slot='text'], p"))
        .map((el) => el.innerHTML.trim())
        .filter(Boolean);

      return {
        src: img?.getAttribute("src") || "",
        srcset: img?.getAttribute("srcset") || "",
        sizes: img?.getAttribute("sizes") || "",
        loading: img?.getAttribute("loading") || "eager",
        alt: img?.getAttribute("alt") || heading,
        heading,
        paragraphs
      };
    }

    imageHTML(data, className, ratioName = "") {
      const attrs = [
        `class="${className}"`,
        ratioName ? `data-psa-ratio="${ratioName}"` : "",
        `src="${data.src}"`,
        data.srcset ? `srcset="${data.srcset}"` : "",
        data.sizes ? `sizes="${data.sizes}"` : "",
        `loading="${data.loading}"`,
        `alt="${data.alt}"`
      ].filter(Boolean).join(" ");

      return `<img ${attrs}>`;
    }

    mediaHTML(data, mediaClass, ratioName) {
      return `
        <div class="psa-parallax__media ${mediaClass}">
          ${this.imageHTML(data, "psa-parallax__media-img", ratioName)}
        </div>
      `;
    }

    copyHTML(data, modifier) {
      return `
        <div class="psa-parallax__copy psa-parallax__copy--${modifier}">
          ${data.heading ? `<h3 class="psa-parallax__copy-heading">${data.heading}</h3>` : ""}
          ${data.paragraphs.map((text) => `<p class="psa-parallax__copy-paragraph">${text}</p>`).join("")}
        </div>
      `;
    }

    mobileCardHTML(data, type) {
      return `
        <section class="psa-parallax__mobile-card">
          <div class="psa-parallax__mobile-media psa-parallax__mobile-media--${type}">
            ${this.imageHTML(data, "psa-parallax__mobile-media-img")}
          </div>
          <div class="psa-parallax__mobile-copy">
            ${data.heading ? `<h3 class="psa-parallax__mobile-copy-heading">${data.heading}</h3>` : ""}
            ${data.paragraphs.map((text) => `<p class="psa-parallax__mobile-copy-paragraph">${text}</p>`).join("")}
          </div>
        </section>
      `;
    }

    render() {
      const [cool, poly, hyper, ice] = this.items.map((item) => this.getItemData(item));
      const title = (this.getAttribute("title") || "").trim();
      const hasTitle = title.length > 0;

      this.insertAdjacentHTML("beforeend", `
        <div class="psa-parallax__pin">
          <div class="psa-parallax__stage">
            ${hasTitle ? `
              <div class="psa-parallax__mobile-title">
                <h4 class="psa-parallax__mobile-title-text">${title.replace(/\s+/, "<br>")}</h4>
              </div>
            ` : ""}

            <div class="psa-parallax__mobile">
              <div class="psa-parallax__mobile-title">
                <h4 class="psa-parallax__mobile-title-text">${title.replace(/\s+/, "<br>")}</h4>
              </div>
              <div class="psa-parallax__mobile-stack">
                ${this.mobileCardHTML(cool, "turbo")}
                ${this.mobileCardHTML(poly, "poly")}
                ${this.mobileCardHTML(hyper, "hyper")}
                ${this.mobileCardHTML(ice, "ice")}
              </div>
            </div>

            <div class="psa-parallax__left-track">
              ${this.mediaHTML(cool, "psa-parallax__media--turbo", "turbo")}
              ${this.mediaHTML(hyper, "psa-parallax__media--hyper", "hyper")}
            </div>

            <div class="psa-parallax__right-track">
              ${this.mediaHTML(poly, "psa-parallax__media--poly", "poly")}
              ${this.mediaHTML(ice, "psa-parallax__media--ice", "ice")}
            </div>

            <div class="psa-parallax__copy-layer">
              ${this.copyHTML(cool, "cool")}
              ${this.copyHTML(poly, "poly")}
              ${this.copyHTML(hyper, "hyper")}
              ${this.copyHTML(ice, "ice")}
            </div>
          </div>
        </div>
      `);

      this.pin = this.querySelector(".psa-parallax__pin");
      this.stage = this.querySelector(".psa-parallax__stage");
    }

    initAnimation() {
      if (!window.gsap || !window.ScrollTrigger) {
        console.warn("[parallax-scroll-animation] GSAP and ScrollTrigger are required.");
        return;
      }

      const root = this;
      const pin = this.pin;
      const stage = this.stage;
      const desktopId = `psa-desktop-${Math.random().toString(36).slice(2)}`;

      gsap.registerPlugin(ScrollTrigger);
      gsap.defaults({ force3D: false });

      const cssNum = (name, fallback = 0) => {
        const value = parseFloat(getComputedStyle(root).getPropertyValue(name));
        return Number.isFinite(value) ? value : fallback;
      };

      const readDesktopRatios = () => {
        root.querySelectorAll("[data-psa-ratio]").forEach((img) => {
          const key = img.dataset.psaRatio;
          if (!key || !img.naturalWidth || !img.naturalHeight) return;
          root.style.setProperty(`--psa-${key}-ratio`, img.naturalHeight / img.naturalWidth);
        });
      };

      const readMobileRatios = () => {
        const pairs = [
          [".psa-parallax__mobile-media--turbo img", "--psa-mobile-turbo-ratio"],
          [".psa-parallax__mobile-media--poly img", "--psa-mobile-poly-ratio"],
          [".psa-parallax__mobile-media--hyper img", "--psa-mobile-hyper-ratio"],
          [".psa-parallax__mobile-media--ice img", "--psa-mobile-ice-ratio"]
        ];

        pairs.forEach(([selector, varName]) => {
          const img = root.querySelector(selector);
          if (img && img.naturalWidth && img.naturalHeight) {
            root.style.setProperty(varName, img.naturalHeight / img.naturalWidth);
          }
        });
      };

      const applyStageFocus = (progress = 0) => {
        const upper = cssNum("--psa-stage-top-upper", 0);
        const lower = cssNum("--psa-stage-top-lower", 0);
        const start = 0.62;
        const end = 0.78;

        if (progress <= start) {
          root.style.setProperty("--psa-stage-top", `${upper}px`);
          return;
        }

        if (progress >= end) {
          root.style.setProperty("--psa-stage-top", `${lower}px`);
          return;
        }

        const raw = (progress - start) / (end - start);
        const eased = raw * raw * (3 - 2 * raw);
        root.style.setProperty("--psa-stage-top", `${gsap.utils.interpolate(upper, lower, eased)}px`);
      };

      const fitMobileStage = () => {
        readMobileRatios();

        const title = root.querySelector(".psa-parallax__mobile-title");
        const medias = Array.from(root.querySelectorAll(".psa-parallax__mobile-media"));
        const titleH = title ? title.offsetHeight : 0;
        const maxImageH = medias.reduce((max, media) => Math.max(max, media.offsetHeight || 0), 0);
        const viewportH = window.innerHeight;
        const titleGap = cssNum("--psa-mobile-title-gap", 10);
        const focusRatio = cssNum("--psa-mobile-focus-ratio", .42);
        const focusY = viewportH * focusRatio;

        let imageTop = focusY - maxImageH / 2;
        if (title) {
          let titleTop = imageTop - titleH - titleGap;
        
          if (titleTop < 16) {
            titleTop = 16;
            imageTop = titleTop + titleH + titleGap;
          }
        
          root.style.setProperty("--psa-mobile-title-top", `${titleTop}px`);
        } else {
          root.style.setProperty("--psa-mobile-title-top", "0px");
        }
        
        root.style.setProperty("--psa-mobile-stack-top", `${imageTop}px`);
      };

      const fitStage = () => {
        readDesktopRatios();

        if (window.innerWidth <= 900) {
          fitMobileStage();
          root.style.setProperty("--psa-stage-scale", 1);
          root.style.setProperty("--psa-stage-top", "0px");
          root.style.setProperty("--psa-content-w", "100%");
          root.style.setProperty("--psa-content-h", "100vh");
          pin.style.minHeight = "0px";
          return;
        }

        const contentW = root.getBoundingClientRect().width;
        const gap = 16;
        const colW = (contentW - gap) / 2;

        const titleEl = root.querySelector(".psa-parallax__title");
        const titleH = titleEl ? Math.max(86, titleEl.offsetHeight || 86) : 0;
        const top = titleEl ? titleH + 32 : 0;

        root.style.setProperty("--psa-title-h", `${titleH}px`);
        root.style.setProperty("--psa-top", `${top}px`);

        const leftH = colW * cssNum("--psa-turbo-ratio", 1);
        const polyH = colW * cssNum("--psa-poly-ratio", .66);
        const hyperH = colW * cssNum("--psa-hyper-ratio", .56);
        const iceH = colW * cssNum("--psa-ice-ratio", 1);

        root.style.setProperty("--psa-content-w", `${contentW}px`);
        root.style.setProperty("--psa-col-w", `${colW}px`);
        root.style.setProperty("--psa-left-h", `${leftH}px`);
        root.style.setProperty("--psa-poly-h", `${polyH}px`);
        root.style.setProperty("--psa-hyper-h", `${hyperH}px`);
        root.style.setProperty("--psa-ice-h", `${iceH}px`);

        const copyInset = Math.max(18, Math.min(28, colW * .047));
        const copyW = Math.max(260, colW - copyInset * 2);
        root.style.setProperty("--psa-copy-inset", `${copyInset}px`);
        root.style.setProperty("--psa-copy-w", `${copyW}px`);

        const polyCopyH = root.querySelector(".psa-parallax__copy--poly")?.offsetHeight || 110;
        const hyperCopyH = root.querySelector(".psa-parallax__copy--hyper")?.offsetHeight || 120;
        const iceCopyH = root.querySelector(".psa-parallax__copy--ice")?.offsetHeight || 120;

        const gapY = 16;
        const iceGap = 18;
        const lowerCopyGap = 30;

        const startRightY = leftH - polyH;
        const midRightY = Math.max(40, startRightY * .26);

        const coolCopyTop = top + Math.max(0, Math.min(18, startRightY * .08));
        const polyCopyTop = top + leftH - polyCopyH;

        const finalLeftY = -Math.min(colW * .52, Math.max(colW * .30, leftH * .42));
        const leftLowerBottom = top + finalLeftY + leftH + gapY + hyperH;
        const finalRightY = leftLowerBottom - top - polyH - iceGap - iceH;
        const rightLowerBottom = top + finalRightY + polyH + iceGap + iceH;

        root.style.setProperty("--psa-cool-copy-top", `${coolCopyTop}px`);
        root.style.setProperty("--psa-poly-copy-top", `${polyCopyTop}px`);
        root.style.setProperty("--psa-hyper-copy-top", `${leftLowerBottom + lowerCopyGap}px`);
        root.style.setProperty("--psa-ice-copy-top", `${rightLowerBottom + lowerCopyGap}px`);

        root.style.setProperty("--psa-start-right-y", `${startRightY}px`);
        root.style.setProperty("--psa-mid-right-y", `${midRightY}px`);
        root.style.setProperty("--psa-final-left-y", `${finalLeftY}px`);
        root.style.setProperty("--psa-final-right-y", `${finalRightY}px`);

        const hyperCopyTop = leftLowerBottom + lowerCopyGap;
        const iceCopyTop = rightLowerBottom + lowerCopyGap;

        const contentH = Math.max(
          top + leftH + gapY + hyperH + lowerCopyGap + hyperCopyH + 80,
          top + polyH + iceGap + iceH + lowerCopyGap + iceCopyH + 80,
          hyperCopyTop + hyperCopyH + 80,
          iceCopyTop + iceCopyH + 80
        );

        root.style.setProperty("--psa-content-h", `${contentH}px`);

        const upperCenter = top + leftH / 2;
        const lowerLeftTop = top + finalLeftY + leftH + gapY;
        const lowerRightTop = top + finalRightY + polyH + iceGap;
        const lowerTop = Math.min(lowerLeftTop, lowerRightTop);
        const lowerCenter = (lowerTop + leftLowerBottom) / 2;

        root.style.setProperty("--psa-stage-scale", 1);
        root.style.setProperty("--psa-stage-top-upper", `${window.innerHeight / 2 - upperCenter}px`);
        root.style.setProperty("--psa-stage-top-lower", `${window.innerHeight / 2 - lowerCenter}px`);
        pin.style.minHeight = "0px";

        const st = ScrollTrigger.getById(desktopId);
        applyStageFocus(st ? st.progress : 0);
      };

      const setTrackToProgress = (progress = 0) => {
        const startRightY = cssNum("--psa-start-right-y", 0);
        const midRightY = cssNum("--psa-mid-right-y", 60);
        const finalRightY = cssNum("--psa-final-right-y", -200);
        const finalLeftY = cssNum("--psa-final-left-y", -300);

        const leftTrack = root.querySelector(".psa-parallax__left-track");
        const rightTrack = root.querySelector(".psa-parallax__right-track");
        const title = root.querySelector(".psa-parallax__title");

        gsap.set(title, progress < .62 ? { autoAlpha: 1, y: 0 } : { autoAlpha: 0, y: -28 });
        applyStageFocus(progress);

        if (progress <= .18) {
          gsap.set(leftTrack, { y: 0 });
          gsap.set(rightTrack, { y: startRightY });
        } else if (progress < .42) {
          const t = (progress - .18) / .24;
          gsap.set(leftTrack, { y: 0 });
          gsap.set(rightTrack, { y: gsap.utils.interpolate(startRightY, midRightY, t) });
        } else if (progress < .58) {
          const t = (progress - .42) / .16;
          gsap.set(leftTrack, { y: 0 });
          gsap.set(rightTrack, { y: gsap.utils.interpolate(midRightY, 0, t) });
        } else if (progress < .62) {
          gsap.set(leftTrack, { y: 0 });
          gsap.set(rightTrack, { y: 0 });
        } else {
          const t = Math.min(1, Math.max(0, (progress - .62) / .16));
          const eased = t * t * (3 - 2 * t);
          gsap.set(leftTrack, { y: gsap.utils.interpolate(0, finalLeftY, eased) });
          gsap.set(rightTrack, { y: gsap.utils.interpolate(0, finalRightY, eased) });
        }
      };

      let isRefreshing = false;
      const refreshLayout = () => {
        if (isRefreshing) return;
        isRefreshing = true;

        fitStage();

        const st = ScrollTrigger.getById(desktopId);
        const progress = st ? st.progress : 0;
        setTrackToProgress(progress);
        applyStageFocus(progress);

        ScrollTrigger.refresh();

        requestAnimationFrame(() => {
          const refreshed = ScrollTrigger.getById(desktopId);
          const refreshedProgress = refreshed ? refreshed.progress : progress;
          setTrackToProgress(refreshedProgress);
          applyStageFocus(refreshedProgress);
          isRefreshing = false;
        });
      };

      root.querySelectorAll("img").forEach((img) => {
        if (!img.complete) img.addEventListener("load", refreshLayout, { once: true });
      });

      window.addEventListener("load", refreshLayout);
      fitStage();

      const mm = gsap.matchMedia();

      mm.add("(min-width: 901px)", () => {
        const leftTrack = root.querySelector(".psa-parallax__left-track");
        const rightTrack = root.querySelector(".psa-parallax__right-track");

        applyStageFocus(0);
        gsap.set(rightTrack, { y: cssNum("--psa-start-right-y", 0) });
        gsap.set(leftTrack, { y: 0 });

        gsap.set(root.querySelector(".psa-parallax__title"), { autoAlpha: 1, y: 0 });
        gsap.set(root.querySelector(".psa-parallax__copy--cool"), { autoAlpha: 0, y: 34 });
        gsap.set(root.querySelector(".psa-parallax__copy--poly"), { autoAlpha: 0, y: 40 });
        gsap.set(root.querySelector(".psa-parallax__copy--hyper"), { autoAlpha: 0, y: 46 });
        gsap.set(root.querySelector(".psa-parallax__copy--ice"), { autoAlpha: 0, y: 48 });

        gsap.set(root.querySelector(".psa-parallax__media--hyper"), { autoAlpha: 0, y: 100, scale: .99 });
        gsap.set(root.querySelector(".psa-parallax__media--ice"), {
          autoAlpha: 0,
          y: 135,
          scale: 1.02,
          clipPath: "inset(14% 0% 0% 0%)"
        });

        const tl = gsap.timeline({
          defaults: { ease: "none" },
          scrollTrigger: {
            id: desktopId,
            trigger: root,
            start: "top top",
            end: () => "+=" + Math.round(Math.max(3600, cssNum("--psa-content-h", 1200) * 3.4)),
            pin,
            scrub: .75,
            anticipatePin: 1,
            invalidateOnRefresh: true,
            onUpdate: (self) => applyStageFocus(self.progress),
            onRefreshInit: () => fitStage(),
            onRefresh: (self) => {
              setTrackToProgress(self.progress);
              applyStageFocus(self.progress);
            }
          }
        });

        tl
          .to(root.querySelector(".psa-parallax__copy--cool"), { autoAlpha: 1, y: 0, duration: .18 }, .02)
          .to({}, { duration: .32 }, .22)
          .fromTo(rightTrack,
            { y: () => cssNum("--psa-start-right-y", 0) },
            { y: () => cssNum("--psa-mid-right-y", 60), force3D: false, immediateRender: false, duration: .48 },
            .56
          )
          .to(root.querySelector(".psa-parallax__copy--cool"), { autoAlpha: 0, y: -28, duration: .42 }, .58)
          .fromTo(rightTrack,
            { y: () => cssNum("--psa-mid-right-y", 60) },
            { y: 0, force3D: false, immediateRender: false, duration: .24 },
            1.08
          )
          .to(root.querySelector(".psa-parallax__copy--poly"), { autoAlpha: 1, y: 0, duration: .22 }, 1.22)
          .to({}, { duration: .72 }, 1.48)
          .to(root.querySelector(".psa-parallax__copy--poly"), { autoAlpha: 0, y: -34, duration: .24 }, 2.22)
          .to(root.querySelector(".psa-parallax__title"), { autoAlpha: 0, y: -28, duration: .22 }, 2.36)
          .to(root.querySelector(".psa-parallax__media--ice"), {
            autoAlpha: 1,
            y: 0,
            scale: 1,
            clipPath: "inset(0% 0% 0% 0%)",
            duration: .26
          }, 2.48)
          .fromTo(leftTrack,
            { y: 0 },
            { y: () => cssNum("--psa-final-left-y", -300), force3D: false, immediateRender: false, duration: .68 },
            2.54
          )
          .fromTo(rightTrack,
            { y: 0 },
            { y: () => cssNum("--psa-final-right-y", -200), force3D: false, immediateRender: false, duration: .68 },
            2.54
          )
          .to(root.querySelector(".psa-parallax__media--hyper"), { autoAlpha: 1, y: 0, scale: 1, duration: .26 }, 2.58)
          .to(root.querySelector(".psa-parallax__copy--hyper"), { autoAlpha: 1, y: 0, duration: .22 }, 3.20)
          .to(root.querySelector(".psa-parallax__copy--ice"), { autoAlpha: 1, y: 0, duration: .22 }, 3.23)
          .to({}, { duration: .62 }, 3.50);

        return () => tl.kill();
      });

      mm.add("(max-width: 900px)", () => {
        fitMobileStage();

        const cards = gsap.utils.toArray(root.querySelectorAll(".psa-parallax__mobile-card"));

        gsap.set(cards, { clearProps: "all" });
        gsap.set(cards[0], { y: 0, scale: 1, autoAlpha: 1 });
        gsap.set(cards.slice(1), { y: "100vh", scale: 1, autoAlpha: 1 });

        const mobileTl = gsap.timeline({
          defaults: { ease: "none" },
          scrollTrigger: {
            trigger: root,
            start: "top top",
            end: "+=2600",
            pin,
            scrub: .65,
            anticipatePin: 1,
            invalidateOnRefresh: true,
            onRefreshInit: () => fitMobileStage()
          }
        });

        mobileTl
          .to(cards[0], { scale: .94, y: 0, duration: .25 }, 0)
          .to(cards[1], { y: 0, duration: .45 }, .08)
          .to({}, { duration: .18 }, .55)
          .to(cards[1], { scale: .955, y: 0, duration: .25 }, .78)
          .to(cards[2], { y: 0, duration: .45 }, .86)
          .to({}, { duration: .18 }, 1.32)
          .to(cards[2], { scale: .965, y: 0, duration: .25 }, 1.55)
          .to(cards[3], { y: 0, duration: .45 }, 1.63)
          .to({}, { duration: .42 }, 2.08);
        return () => mobileTl.kill();
      });

      let throttleTimer = null;
      let trailingTimer = null;
      let pending = false;

      const scheduleRefresh = () => {
        pending = true;

        if (!throttleTimer) {
          throttleTimer = setTimeout(() => {
            throttleTimer = null;
            if (pending) {
              pending = false;
              requestAnimationFrame(refreshLayout);
            }
          }, 160);
        }

        clearTimeout(trailingTimer);
        trailingTimer = setTimeout(() => {
          pending = false;
          requestAnimationFrame(() => {
            refreshLayout();
            requestAnimationFrame(refreshLayout);
          });
        }, 260);
      };

      window.addEventListener("resize", scheduleRefresh);
      window.addEventListener("orientationchange", scheduleRefresh);
      if (window.visualViewport) window.visualViewport.addEventListener("resize", scheduleRefresh);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(refreshLayout);
    }
  }

  customElements.define("parallax-scroll-animation", ParallaxScrollAnimation);
})();
